import { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Container } from "@shared/components/Container";
import { RichTextContent, SeoHead } from "@shared/components";
import { ProductImageCarousel, ProductCarousel } from "@shared/components/carousels";
import type { CarouselProduct } from "@shared/types/carousel";
import { useProductBySlug, useRelatedProducts } from "@shared/hooks/useProducts";
import { useCartNotifications } from "@shared/hooks/useNotifications";
import { useCartStore } from "@public/stores/cartStore";
import { useForm } from "react-hook-form";
import {
  productCardQuantityResolver,
  type ProductCardQuantityForm,
} from "@shared/schemas";
import { formatUnit } from "@shared/utils";
import { generateProductSchema, generateBreadcrumbSchema } from "@shared/utils/schemaOrg";
import { useCategory } from "@shared/hooks/useCategories";

export function ProductDetail() {
  const { productSlug } = useParams<{
    productSlug: string;
  }>();
  const location = useLocation();
  const productFromState = location.state?.product;

  // Hook para obtener el producto (solo si no viene del estado)
  const {
    data: productFromApi,
    isLoading,
    error,
  } = useProductBySlug(productSlug || "", {
    enabled: !productFromState, // Solo hacer petición si no hay estado
  });

  // Usar producto del estado o de la API
  const product = productFromState || productFromApi;

  // Obtener categoría del producto para breadcrumb
  const { data: category } = useCategory(product?.categoryId || "");

  // Obtener productos relacionados
  const {
    data: relatedProducts = [],
  } = useRelatedProducts(product?.id || "", 6, {
    enabled: !!product?.id, // Solo hacer petición si hay producto
  });

  // Convertir productos relacionados a formato CarouselProduct
  const carouselProducts: CarouselProduct[] = useMemo(() => {
    const defaultImage =
      "https://res.cloudinary.com/dq8ig7aly/image/upload/v1749660976/no-image_lbt2of.jpg";
    
    return relatedProducts.map((product) => {
      const imageUrl = product.images?.[0]?.url || defaultImage;
      return {
        _id: product.id,
        name: product.name,
        slug: product.slug,
        images: [
          {
            url: imageUrl,
            publicId: imageUrl,
            _id: `${product.id}-image`,
          },
        ],
      };
    });
  }, [relatedProducts]);

  // Log para ver la estructura completa del producto
  console.log("📦 Product Detail - Product Data:", {
    productFromState: productFromState ? "✅ Present" : "❌ Not present",
    productFromApi: productFromApi ? "✅ Present" : "❌ Not present",
    product: product ? {
      id: product.id,
      name: product.name,
      slug: product.slug,
      brand: product.brand,
      unitOfMeasurement: product.unitOfMeasurement,
      description: product.description ? "✅ Present" : "❌ Not present",
      categoryId: product.categoryId,
      images: product.images?.length || 0,
      // Mostrar TODOS los campos del producto para ver si hay algo relacionado con m² por unidad
      fullProduct: product,
      // Campos relacionados con m² o cobertura (explorar si existen)
      allProductKeys: product ? Object.keys(product) : [],
    } : null,
  });
  
  // Log adicional: expandir el fullProduct para ver TODOS los campos y valores
  if (product) {
    console.log("📦 Product Detail - Full Product Object:", product);
    console.log("📦 Product Detail - Product Keys:", Object.keys(product));
    // Buscar campos que puedan contener información sobre m² por unidad
    const possibleM2Fields = Object.keys(product).filter(key => 
      key.toLowerCase().includes('m2') || 
      key.toLowerCase().includes('meter') || 
      key.toLowerCase().includes('coverage') || 
      key.toLowerCase().includes('area') ||
      key.toLowerCase().includes('square')
    );
    if (possibleM2Fields.length > 0) {
      console.log("🔍 Product Detail - Possible m² related fields:", possibleM2Fields);
    }
  }

  // Store del carrito
  const addToCart = useCartStore((state) => state.addToCart);
  const openCart = useCartStore((state) => state.openCart);
  
  // Notificaciones de carrito
  const { notifyAddToCart } = useCartNotifications();

  // Formulario para cantidad
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
    reset,
  } = useForm<ProductCardQuantityForm>({
    resolver: productCardQuantityResolver.resolver,
    defaultValues: productCardQuantityResolver.defaultValues,
    mode: productCardQuantityResolver.mode,
  });

  const quantity = watch("quantity");


  // Obtener textos de unidad
  const unitText = useMemo(() => {
    return product ? formatUnit(product.unitOfMeasurement) : "unidades";
  }, [product]);

  const handleQuantityChange = (newQuantity: number) => {
    setValue("quantity", newQuantity, { shouldValidate: true });
  };

  const onSubmit = (data: ProductCardQuantityForm) => {
    if (data.quantity > 0 && product) {
      addToCart(product, data.quantity);
      notifyAddToCart(product.name, data.quantity, product.unitOfMeasurement);
      openCart(); // Abrir el carrito después de agregar
      reset();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-200 rounded-lg"></div>
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-6 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Container variant="default" padding="lg" className="text-center">
            <div className="text-red-600">
              <p>
                Error al cargar el producto:{" "}
                {error?.message || "Producto no encontrado"}
              </p>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  // Generar schemas para SEO
  const productSchema = product ? generateProductSchema(product) : null;
  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    ...(category ? [{ name: category.name, url: `/catalog/${category.slug}` }] : []),
    ...(product ? [{ name: product.name, url: `/producto/${product.slug}` }] : []),
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);

  const schemas = [productSchema, breadcrumbSchema].filter(Boolean) as object[];

  return (
    <>
      {product && (
        <SeoHead
          product={product}
          canonical={`/producto/${product.slug}`}
          ogType="product"
          ogImage={product.primaryImage || product.images?.[0]?.url}
          schemaJsonLd={schemas}
        />
      )}
      <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb y botón volver */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-gray-600 hover:text-[#282828] transition-colors font-medium"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Volver
          </button>
        </div>

        {/* Header del producto (fuera del grid sticky) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
          <div className="border-b border-gray-200 pb-4">
            <p className="text-sm text-gray-500 mb-2 font-medium uppercase tracking-wide">{product.brand}</p>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 leading-tight">
              {product.name}
            </h1>
          </div>
        </div>

        {/* Contenido principal con imagen sticky */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Galería de imágenes - Sticky */}
            <div className="lg:sticky lg:top-20 lg:self-start">
              <div className="space-y-4">
                <ProductImageCarousel
                  images={product.images || []}
                  productName={product.name}
                  className="h-full"
                />
              </div>
            </div>

            {/* Información del producto - Scroll independiente */}
            <div className="space-y-6">
              {/* Descripción */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Descripción
                </h3>
                <RichTextContent
                  content={product.description}
                  className="text-gray-600 leading-relaxed prose prose-sm max-w-none"
                  emptyPlaceholder="Este producto aún no tiene una descripción detallada."
                />
              </div>

              {/* Selector de cantidad */}
              <div className="space-y-4 border-b border-gray-200 pb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Cantidad {unitText !== "unidades" ? `(${unitText})` : ""}
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 0}
                      className="w-12 h-12 rounded-lg border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 hover:border-[#282828] hover:text-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold text-lg"
                    >
                      −
                    </button>

                    <input
                      type="number"
                      {...register("quantity", { valueAsNumber: true })}
                      min="0"
                      max="10000"
                      className="w-24 px-4 py-3 text-center border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] font-semibold text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />

                    <button
                      type="button"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      className="w-12 h-12 rounded-lg border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 hover:border-[#282828] hover:text-[#282828] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold text-lg"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Botones de acción */}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 pt-4">
                <button
                  type="submit"
                  disabled={quantity <= 0 || !isValid}
                  className="w-full bg-[#282828] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#1a1a1a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  Presupuestar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (quantity > 0 && product) {
                      addToCart(product, quantity);
                      notifyAddToCart(product.name, quantity, product.unitOfMeasurement);
                      reset();
                    }
                  }}
                  disabled={quantity <= 0}
                  className="w-full bg-white text-[#282828] py-3 px-6 rounded-lg font-medium border-2 border-[#282828] hover:bg-[#282828] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  Agregar al presupuesto
                </button>
              </form>
            </div>
          </div>
        </div>


        {/* Productos relacionados */}
        {carouselProducts.length > 0 && (
          <div className="mt-16">
            <ProductCarousel
              title="¡Llévalos juntos!"
              products={carouselProducts}
              className="relative"
            />
          </div>
        )}
      </div>
    </div>
    </>
  );
}
